import React, { useEffect, useState, useRef, useMemo } from "react";
import { Play, Image as ImageIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Gallery({ items = [], loading = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const sectionRef = useRef(null);

  const getYouTubeID = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
          setIsVisible(true);
          observer.unobserve(section);
        }
      },
      {
        threshold: [0, 0.1, 0.2],
        rootMargin: "50px"
      },
    );

    observer.observe(section);

    // Immediate check
    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsVisible(true);
    }

    return () => {
      if (section) observer.unobserve(section);
      observer.disconnect();
    };
  }, []);

  const dynamicMinHeight = useMemo(() => {
    if (loading) return 1000;
    if (items.length === 0) return 400;
    // Estimated height: ~350px per "row" of 5 on desktop
    if (items.length <= 5) return 450;
    if (items.length <= 10) return 750;
    return 1000;
  }, [items.length, loading]);


  const SkeletonPolaroid = ({ index }) => (
    <div className={`polaroid-item polaroid-${index + 1} polaroid-visible animate-pulse`}>
      <div className="polaroid-frame border-black/20 shadow-lg">
        <div className="polaroid-content bg-gray-200" />
        <div className="h-4 bg-gray-200 mt-4 mx-auto w-3/4 rounded" />
      </div>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      id="gallery"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white overflow-hidden"
      style={{
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Section header */}
        <div
          className={`text-center mb-12 sm:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-normal text-black mb-3 sm:mb-4"
            style={{
              fontFamily: "Playfair Display, Georgia, serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            Gallery
          </h2>
          <p
            className="text-base sm:text-lg text-black font-medium max-w-[600px] mx-auto px-4"
            style={{
              fontFamily: "Crimson Pro, Georgia, serif",
              fontStyle: "italic",
            }}
          >
            Moments captured in time, memories made through music
          </p>
        </div>

        {/* Video Modal */}
        <AnimatePresence>
          {activeVideo && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-sm"
                onClick={() => setActiveVideo(null)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="relative w-full max-w-5xl aspect-video bg-black shadow-2xl rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => setActiveVideo(null)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all shadow-lg backdrop-blur-md"
                >
                  <X size={20} />
                </button>
                {activeVideo.youtube_link ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeID(activeVideo.youtube_link)}?autoplay=1&controls=1&rel=0&modestbranding=1&fs=0&disablekb=1&playsinline=1&iv_load_policy=3`}
                    className="w-full h-full"
                    title={activeVideo.caption}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={activeVideo.file_path} controls className="w-full h-full" />
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Image Modal (Polaroid Style) */}
        <AnimatePresence>
          {activeImageIndex !== null && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/95 backdrop-blur-sm"
                onClick={() => setActiveImageIndex(null)}
              />


              <motion.div
                initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.9, rotate: 2 }}
                className="relative bg-white p-3 sm:p-5 shadow-2xl max-w-[90vw] sm:max-w-4xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setActiveImageIndex(null)}
                  className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors flex items-center gap-2"
                >
                  <span className="text-xs font-bold tracking-widest uppercase">Close</span>
                  <X size={24} />
                </button>

                <div className="relative overflow-hidden bg-gray-100">
                  <img
                    src={items[activeImageIndex].file_path}
                    alt={items[activeImageIndex].caption}
                    className="max-h-[70vh] w-auto object-contain mx-auto"
                  />
                </div>

                <div className="mt-4 sm:mt-6 text-center">
                  <p
                    className="text-black text-xl sm:text-3xl mb-1 px-4"
                    style={{ fontFamily: "Gloria Hallelujah, cursive" }}
                  >
                    {items[activeImageIndex].caption}
                  </p>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Polaroid Gallery */}
        <div
          className="gallery-container"
          style={{ '--gallery-min-height': `${dynamicMinHeight}px` }}
        >
          <div className="gallery-layout-wrapper">
            {loading ? (
              Array(8).fill(0).map((_, i) => <SkeletonPolaroid key={i} index={i} />)
            ) : items.length > 0 ? (
              items.map((item, index) => (
                <div
                  key={item.id || index}
                  className={`polaroid-item polaroid-${index + 1} ${isVisible ? "polaroid-visible" : ""
                    }`}
                  onClick={() => {
                    if (item.type === "video") {
                      setActiveVideo(item);
                    } else {
                      setActiveImageIndex(index);
                    }
                  }}
                >
                  <div className="polaroid-frame group/polaroid">
                    <div className="polaroid-content">
                      {item.type === "video" ? (
                        <div className="relative w-full h-full bg-black">
                          {item.youtube_link ? (
                            <img
                              src={`https://img.youtube.com/vi/${getYouTubeID(item.youtube_link)}/maxresdefault.jpg`}
                              alt={item.caption}
                              className="polaroid-image opacity-60 group-hover/polaroid:opacity-90 transition-opacity duration-500"
                              onError={(e) => { e.target.src = "https://via.placeholder.com/800x450?text=YouTube+Video"; }}
                            />
                          ) : (
                            <video
                              src={item.file_path}
                              poster={item.file_path.replace('.mp4', '.jpg')}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover opacity-80 group-hover/polaroid:opacity-100 transition-opacity duration-500"
                              onMouseEnter={(e) => { try { e.target.play(); } catch { } }}
                              onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch { } }}
                            />
                          )}
                          {/* Play icon overlay */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover/polaroid:scale-110 transition-transform duration-300">
                            <div className="w-12 h-12 bg-black/60 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                              <Play size={20} className="text-white ml-0.5" fill="white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.file_path}
                          alt={item.caption}
                          className="polaroid-image"
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Caption */}
                    <p
                      className="polaroid-caption"
                      style={{ fontFamily: "Gloria Hallelujah, cursive" }}
                    >
                      {item.caption}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center border border-dashed border-black/30 rounded-xl bg-gray-50">
                <ImageIcon size={48} className="mx-auto text-black/40 mb-4" />
                <p className="text-black font-serif italic text-sm uppercase tracking-widest">Captured moments coming soon.</p>
              </div>
            )}
          </div>
        </div>

        {/* View gallery link */}
        <div
          className={`text-center mt-12 sm:mt-16 transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <Link
            to="/gallery/images"
            className="inline-block border-b-2 border-black text-black font-medium hover:border-black/40 hover:text-black/60 transition-all duration-300 touch-manipulation py-1"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "14px",
              letterSpacing: "0.5px",
            }}
          >
            VIEW FULL GALLERY
          </Link>
        </div>
      </div>

      <style jsx="true">{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500&family=Crimson+Pro:ital,wght@0,400;1,400&family=Gloria+Hallelujah&display=swap');

        /* Mobile-first: Grid layout by default */
        .gallery-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          position: relative;
        }

        @media (min-width: 640px) {
          .gallery-container {
            grid-template-columns: repeat(2, 1fr);
            gap: 32px;
          }
        }

        @media (min-width: 768px) {
          .gallery-container {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Desktop: Switch to absolute positioning for scattered effect */
        @media (min-width: 1024px) {
          .gallery-container {
            display: block;
            min-height: var(--gallery-min-height, 1000px);
            position: relative;
          }

          .polaroid-item {
            position: absolute;
          }
        }

        .polaroid-item {
          opacity: 0;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          margin: 0 auto;
          max-width: 320px;
          width: 100%;
        }

        .polaroid-visible {
          opacity: 1;
        }

        .polaroid-frame {
          background: white;
          padding: 12px;
          padding-bottom: 45px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.08);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          height: 100%;
        }

        .polaroid-item:hover .polaroid-frame {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
          z-index: 100;
        }

        .polaroid-content {
          position: relative;
          width: 100%;
          height: 280px;
          background: #f5f5f5;
          overflow: hidden;
        }

        .polaroid-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .polaroid-caption {
          text-align: center;
          margin-top: 12px;
          font-size: 13px;
          color: rgba(0, 0, 0, 0.9);
          font-weight: 600;
        }

        /* Desktop scattered positioning */
        @media (min-width: 1024px) {
          .polaroid-1 {
            top: 0%;
            left: 5%;
            width: 200px;
            transform: rotate(-4deg) translateY(50px);
          }
          .polaroid-1.polaroid-visible {
            transform: rotate(-4deg) translateY(0);
            transition-delay: 0ms;
          }

          .polaroid-2 {
            top: 3%;
            left: 22%;
            width: 180px;
            transform: rotate(6deg) translateY(50px);
          }
          .polaroid-2.polaroid-visible {
            transform: rotate(6deg) translateY(0);
            transition-delay: 100ms;
          }

          .polaroid-3 {
            top: 2%;
            left: 40%;
            width: 190px;
            transform: rotate(-2deg) translateY(50px);
          }
          .polaroid-3.polaroid-visible {
            transform: rotate(-2deg) translateY(0);
            transition-delay: 200ms;
          }

          .polaroid-4 {
            top: 0%;
            left: 58%;
            width: 175px;
            transform: rotate(8deg) translateY(50px);
          }
          .polaroid-4.polaroid-visible {
            transform: rotate(8deg) translateY(0);
            transition-delay: 300ms;
          }

          .polaroid-5 {
            top: 5%;
            left: 75%;
            width: 185px;
            transform: rotate(-5deg) translateY(50px);
          }
          .polaroid-5.polaroid-visible {
            transform: rotate(-5deg) translateY(0);
            transition-delay: 400ms;
          }

          .polaroid-6 {
            top: 25%;
            left: 3%;
            width: 195px;
            transform: rotate(3deg) translateY(50px);
          }
          .polaroid-6.polaroid-visible {
            transform: rotate(3deg) translateY(0);
            transition-delay: 500ms;
          }

          .polaroid-7 {
            top: 28%;
            left: 21%;
            width: 180px;
            transform: rotate(-7deg) translateY(50px);
          }
          .polaroid-7.polaroid-visible {
            transform: rotate(-7deg) translateY(0);
            transition-delay: 600ms;
          }

          .polaroid-8 {
            top: 27%;
            left: 39%;
            width: 190px;
            transform: rotate(4deg) translateY(50px);
          }
          .polaroid-8.polaroid-visible {
            transform: rotate(4deg) translateY(0);
            transition-delay: 700ms;
          }

          .polaroid-9 {
            top: 26%;
            left: 57%;
            width: 185px;
            transform: rotate(-3deg) translateY(50px);
          }
          .polaroid-9.polaroid-visible {
            transform: rotate(-3deg) translateY(0);
            transition-delay: 800ms;
          }

          .polaroid-10 {
            top: 30%;
            left: 76%;
            width: 175px;
            transform: rotate(7deg) translateY(50px);
          }
          .polaroid-10.polaroid-visible {
            transform: rotate(7deg) translateY(0);
            transition-delay: 900ms;
          }

          .polaroid-11 {
            top: 52%;
            left: 4%;
            width: 190px;
            transform: rotate(-6deg) translateY(50px);
          }
          .polaroid-11.polaroid-visible {
            transform: rotate(-6deg) translateY(0);
            transition-delay: 1000ms;
          }

          .polaroid-12 {
            top: 54%;
            left: 22%;
            width: 185px;
            transform: rotate(5deg) translateY(50px);
          }
          .polaroid-12.polaroid-visible {
            transform: rotate(5deg) translateY(0);
            transition-delay: 1100ms;
          }

          .polaroid-13 {
            top: 53%;
            left: 40%;
            width: 180px;
            transform: rotate(-4deg) translateY(50px);
          }
          .polaroid-13.polaroid-visible {
            transform: rotate(-4deg) translateY(0);
            transition-delay: 1200ms;
          }

          .polaroid-14 {
            top: 55%;
            left: 58%;
            width: 195px;
            transform: rotate(6deg) translateY(50px);
          }
          .polaroid-14.polaroid-visible {
            transform: rotate(6deg) translateY(0);
            transition-delay: 1300ms;
          }

          .polaroid-15 {
            top: 52%;
            left: 75%;
            width: 180px;
            transform: rotate(-5deg) translateY(50px);
          }
          .polaroid-15.polaroid-visible {
            transform: rotate(-5deg) translateY(0);
            transition-delay: 1400ms;
          }

          .polaroid-16 {
            top: 77%;
            left: 12%;
            width: 190px;
            transform: rotate(4deg) translateY(50px);
          }
          .polaroid-16.polaroid-visible {
            transform: rotate(4deg) translateY(0);
            transition-delay: 1500ms;
          }

          .polaroid-content {
            height: 240px;
          }

          .polaroid-item:hover .polaroid-frame {
            transform: translateY(-8px) rotate(0deg) !important;
          }
        }
      `}</style>
    </section>
  );
}
